import { apiClient } from "../client";

/**
 * Verified 2026-07 against the live API (admin.astrorajumaharaj.com):
 *   GET /v1/courses?page=1&per_page=2
 *   GET /v1/courses/:id
 *   GET /v1/courses/slug/:slug
 *   GET /v1/courses?search=demo&level=beginner
 * The actual payload shape differs substantially from the Postman
 * collection's prose descriptions (which read like they were written
 * against a different/generic branch) — this backend has a bespoke API
 * resource that already camelCases everything and pre-shapes courses very
 * close to a typical frontend's display model. Types below reflect the
 * confirmed live shape, not the Postman docs.
 */

export interface CourseInstructor {
  id: number;
  name: string;
  photo: string | null;
  designation: string | null;
  experience: string | null;
  bio: string | null;
  totalStudents: number | null;
  totalCourses: number | null;
  [key: string]: unknown;
}

/** All instructors of a course. Re-verified live 2026-07-18: both the list
 * and detail payloads now carry `instructors` (an ARRAY — courses can be
 * co-taught, matching `instructor_ids` on the create side); the old singular
 * `instructor` key is GONE from the live payload. Read through this helper so
 * consumers survive either shape. */
export function courseInstructors(course: {
  instructors?: CourseInstructor[] | null;
  instructor?: CourseInstructor | null;
}): CourseInstructor[] {
  if (Array.isArray(course?.instructors)) return course.instructors;
  if (course?.instructor) return [course.instructor];
  return [];
}

/** The course's first-listed instructor (what single-instructor UIs show). */
export function primaryInstructor(course: {
  instructors?: CourseInstructor[] | null;
  instructor?: CourseInstructor | null;
}): CourseInstructor | null {
  return courseInstructors(course)[0] ?? null;
}

/** One lesson inside a curriculum session. No duration/preview/lock flags
 * are present on the live payload (unlike the Postman prose, which promised
 * `locked`/`videoUrl` gating) — access is enforced at the course level via
 * `visibility` + enrollment instead. */
export interface CourseLesson {
  id: number;
  title: string;
  /** e.g. "vimeo". */
  type?: string | null;
  /** NOTE: live data has been observed with a duplicated
   * "https://vimeo.com/" prefix (a backend data-entry bug) — always read
   * this through `cleanVideoUrl()` rather than using it raw. */
  videoUrl: string | null;
  [key: string]: unknown;
}

/** A grouped block of lessons ("Session 1", "Session 2", …) — the live API
 * already groups lessons server-side, so no client-side grouping is needed. */
export interface CourseCurriculumSession {
  id: number;
  title: string;
  lessons: CourseLesson[];
}

/** A live class as embedded directly in the course detail payload — already
 * scoped to this course, so no separate cross-referencing query is needed.
 * Only id/title/date/time/status have been confirmed live; other fields are
 * speculative and optional. */
export interface EmbeddedLiveClass {
  id: number;
  title: string;
  /** "YYYY-MM-DD" */
  date: string;
  /** "HH:mm:ss" */
  time: string;
  status: "upcoming" | "live" | "completed" | "cancelled" | string;
  instructor?: string | null;
  duration?: string | null;
  [key: string]: unknown;
}

export interface EmbeddedRecording {
  id: number;
  title: string;
  date?: string | null;
  duration?: string | null;
  recordingUrl?: string | null;
  [key: string]: unknown;
}

/** Course record as returned by GET /courses (list) and GET /courses/:id |
 * /courses/slug/:slug (detail — adds `curriculum` + live-class fields). */
export interface Course {
  id: number;
  slug: string;
  title: string;
  /** HTML blurb. */
  shortDescription: string;
  coverImage: string | null;
  /** Category name directly (not an id/object). For category ids (the
   * create form needs them), use GET /course-categories via `categories()`
   * — re-verified working live 2026-07-18. */
  category: string;
  language: string | null;
  level: "beginner" | "intermediate" | "advanced" | string;
  courseType: "recorded" | "live" | string;
  visibility: "public" | "private" | string;
  /** "YYYY-MM-DD" */
  lastUpdated: string | null;
  totalReviews: number;
  studentsEnrolled: number;
  certificateAvailable: boolean;
  lifetimeAccess: boolean;
  /** Plain number (not a decimal string). */
  price: number;
  discountPrice: number | null;
  currency: string;
  /** Part payment, added to the API 2026-10-29. Whether the student may pay
   * this course off in stages rather than in one go. */
  acceptPartPayment?: boolean;
  /** The floor on the first payment when `acceptPartPayment` is on. */
  minPartPrice?: number | null;
  /** The fixed installment schedule, when the course has one. Only present on
   * the single-course read; an empty array means part payment is open-balance
   * (pay at least `minPartPrice`, then clear the rest whenever). */
  installments?: CourseInstallment[];
  /** Unit unconfirmed (hours vs minutes) — null on every course seen so far. */
  totalDuration: number | null;
  descriptionParagraphs: string[];
  descriptionBullets: string[];
  descriptionImage: string | null;
  descriptionImageCaption: string | null;
  /** All instructors (array — live shape as of 2026-07-18). Prefer the
   * `courseInstructors()` / `primaryInstructor()` helpers over reading this
   * (or the removed singular `instructor`) directly. */
  instructors: CourseInstructor[];
  /** @deprecated Old singular shape — no longer present on the live payload;
   * kept optional so stale cached responses don't explode. */
  instructor?: CourseInstructor | null;
  /** Only present on the single-course read. */
  curriculum?: CourseCurriculumSession[];
  upcomingLiveClasses?: EmbeddedLiveClass[];
  previousRecordings?: EmbeddedRecording[];
  [key: string]: unknown;
}

/** One scheduled payment in a course's fixed installment plan.
 *
 * `dueDays` is an offset in days from the student's own enrollment, NOT a
 * calendar date — students buy a recorded course whenever they like, so a fixed
 * date would already be overdue for everyone enrolling after it. Installment 1
 * is normally `dueDays: 0`, payable at checkout. */
export interface CourseInstallment {
  id: number;
  position: number;
  label: string | null;
  amount: number;
  dueDays: number;
}

/** PUT /courses/:id/installments body — the WHOLE plan, every time. The server
 * replaces it wholesale and rejects anything whose amounts don't add up to the
 * course price, so a partial send would be meaningless. An empty array clears
 * the plan back to open-balance part payment. */
export interface SaveInstallmentsPayload {
  installments: Array<{
    label?: string | null;
    amount: number;
    /** Days after enrollment. Must not go backwards between installments. */
    due_days: number;
  }>;
}

/** GET|PUT /courses/:id/installments — note the snake_case rows here: this is
 * the admin envelope (sendSuccess), not the camelCase customer Resource. */
export interface CourseInstallmentsResponse {
  success?: boolean;
  message?: string;
  data?: {
    course_id: number;
    price?: number;
    accept_part_payment?: boolean;
    min_part_price?: number | null;
    installments: Array<{
      id: number;
      position: number;
      label: string | null;
      amount: string | number;
      due_days: number;
    }>;
  };
  [key: string]: unknown;
}

/** GET /courses/:id/outstanding — who on this course still owes money. */
export interface CourseOutstandingResponse {
  success?: boolean;
  message?: string;
  data?: {
    course: { id: number; title: string; price: number; accept_part_payment: boolean };
    total_enrollments: number;
    total_collected: number;
    total_outstanding: number;
    items: Array<{
      enrollment_id: number;
      status: string;
      purchased_at: string | null;
      user: { id: number; first_name: string; last_name: string | null; email: string | null; mobile_no: string | null };
      paid: number;
      balance: number;
      next_installment: { id: number; position: number; label: string | null; amount: number; due_date: string | null } | null;
    }>;
  };
  [key: string]: unknown;
}

/** GET /courses/:id | /courses/slug/:slug — `{ data: Course, success }`. */
export interface CourseEnvelope {
  success: boolean;
  data: Course;
}

/** GET /courses (list) — a Laravel API Resource collection: flat `data`
 * array (NOT nested one level deeper), pagination under `meta`, `links` for
 * prev/next. Distinct from the shop catalog's `Envelope<LaravelPaginator<T>>`
 * shape — do not reuse that type here. */
export interface CoursesListResponse {
  data: Course[];
  links: {
    first: string | null;
    last: string | null;
    prev: string | null;
    next: string | null;
  };
  meta: {
    current_page: number;
    from: number | null;
    last_page: number;
    path: string;
    per_page: number;
    to: number | null;
    total: number;
  };
  success: boolean;
}

export interface CourseListParams {
  page?: number;
  /** ⚠ IGNORED by the live backend (re-verified 2026-07-18: asked 3, got the
   * server default of 20). Page through `meta.last_page` instead of assuming
   * a big per_page returned everything. */
  per_page?: number;
  /** Confirmed working live — matches title/description. */
  search?: string;
  /** Confirmed working live — matches `level` exactly ("beginner" | "intermediate" | "advanced"). */
  level?: string;
  [key: string]: unknown;
}

export interface Enrollment {
  id: number;
  user_id?: number;
  course_id?: number;
  course?: Course | null;
  status?: string | null;
  purchased_at?: string | null;
  amount_paid?: string | number | null;
  payment_reference?: string | null;
  /** What this student has paid and still owes on the course (added to the API
   * 2026-10-29). Absent for a free course or a live-class enrollment — there is
   * no balance to speak of, so absence means "not applicable", not "nothing
   * due". This is the only place the customer API says what THIS person owes:
   * the course read publishes the terms, not the personal balance. */
  payment?: EnrollmentPaymentState | null;
  [key: string]: unknown;
}

/** One student's standing on one priced course. */
export interface EnrollmentPaymentState {
  price: number;
  paid: number;
  balance: number;
  isFullyPaid: boolean;
  acceptPartPayment: boolean;
  /** The instalment being chased, carrying the student's own due date. Null when
   * nothing is outstanding, or on an open-balance course with no fixed plan. */
  nextInstallment?: {
    id: number;
    position: number;
    label: string | null;
    amount: number;
    due_date: string | null;
    outstanding: number;
    state: string;
  } | null;
  /** What a "pay the next instalment" button will charge. Null when settled. */
  nextAmount?: number | null;
}

/** UNVERIFIED against a live account (requires auth) — kept permissive. */
export interface EnrollmentsEnvelope {
  success: boolean;
  data: { data: Enrollment[] } | Enrollment[];
}

export interface EnrollmentEnvelope {
  success: boolean;
  data: Enrollment;
}

export interface EnrollPayload {
  user_id: number | string;
  course_id: number | string;
  status?: string;
  purchased_at?: string;
  [key: string]: unknown;
}

/** UNVERIFIED against a live enrolled account — field names are taken
 * verbatim from the Postman prose ("Returns progress_percent,
 * completed_lesson_ids, total_lessons, course_completed,
 * certificate_issued"), which is the same class of description that turned
 * out accurate for other prose-only fields, but treat with more caution
 * than the Course shape above since it couldn't be confirmed with a real
 * request. */
export interface CourseProgress {
  progress_percent: number;
  completed_lesson_ids: Array<number | string>;
  total_lessons: number;
  course_completed: boolean;
  certificate_issued: boolean;
  [key: string]: unknown;
}

export interface CourseProgressEnvelope {
  success: boolean;
  data: CourseProgress;
}

export interface ValidateCouponPayload {
  code: string;
}

export interface ValidateCouponResponse {
  success?: boolean;
  valid?: boolean;
  message?: string;
  discount_amount?: number | string;
  final_amount?: number | string;
  [key: string]: unknown;
}

export interface CourseCheckoutOrderPayload {
  coupon_code?: string;
  /** "full" (the default, and the whole remaining balance) or "installment"
   * (the next scheduled payment). Only honoured on a course whose
   * `acceptPartPayment` is on; a coupon cannot be combined with it. */
  mode?: "full" | "installment";
}

/** Razorpay bootstrap data for a course purchase. */
export interface CourseCheckoutOrderResponse {
  success?: boolean;
  status?: boolean;
  message?: string;
  order_id?: string;
  razorpay_order_id?: string;
  amount?: number | string;
  currency?: string;
  key?: string;
  [key: string]: unknown;
}

export interface CourseCheckoutVerifyPayload {
  razorpay_payment_id: string;
  razorpay_order_id?: string;
  razorpay_signature?: string;
  coupon_code?: string;
  [key: string]: unknown;
}

export interface CourseCheckoutVerifyResponse {
  success?: boolean;
  status?: boolean;
  message?: string;
  enrollment?: Enrollment;
  data?: Enrollment;
  [key: string]: unknown;
}

/* ── Instructor course-authoring (POST) ─────────────────────────────────
 * Create-side endpoints for the dashboard's instructor console. Unlike the
 * reads above (verified against the live API), these follow the documented
 * POST contract: POST /courses, POST /courses/:course/lessons, plus the
 * GET /course-categories lookup that feeds the create form's category select
 * — the read side deliberately has no category list (see `category` on
 * Course above), but the authoring form needs real category ids. */

/** GET /course-categories row — a category the create form can offer. Shape
 * kept permissive (name vs title); unwrapped on the client. */
export interface CourseCategory {
  id: number;
  name?: string | null;
  title?: string | null;
  slug?: string | null;
  [key: string]: unknown;
}

/** GET /course-categories envelope — bare array or `{ data }` (possibly a
 * nested paginator); consumers unwrap with the dashboard's toList(). */
export interface CourseCategoriesResponse {
  success?: boolean;
  data?: CourseCategory[] | { data: CourseCategory[]; [key: string]: unknown };
  [key: string]: unknown;
}

/** POST /courses body. Only title + course_category_id + difficulty_level
 * are required; every other field is optional and omitted when left blank. */
export interface CreateCoursePayload {
  title: string;
  course_category_id: number | string;
  difficulty_level: "beginner" | "intermediate" | "advanced" | string;
  summary?: string;
  price?: number;
  discount_price?: number;
  currency?: string;
  /** Part payment. `min_part_price` is required whenever this is true, and
   * must be below `price` — the API 422s otherwise. */
  accept_part_payment?: boolean;
  min_part_price?: number | null;
  status?: "draft" | "published" | string;
  course_type?: "recorded" | "live" | string;
  max_students?: number;
  /** "YYYY-MM-DD". */
  enrollment_expiration?: string;
  is_public?: boolean;
  enable_qa?: boolean;
  /** Free-text, e.g. "10h 30m". */
  total_duration?: string;
  language?: string;
  certificate_available?: boolean;
  lifetime_access?: boolean;
  /** Admin / co-teaching only — omitted by default so the course is owned by
   * the authenticated instructor server-side. */
  instructor_ids?: Array<number | string>;
  [key: string]: unknown;
}

/** The partial course echoed back by POST /courses (not the full read shape). */
export interface CreatedCourse {
  id: number;
  title: string;
  slug?: string;
  status?: string;
  [key: string]: unknown;
}

export interface CreateCourseResponse {
  success?: boolean;
  message?: string;
  data?: CreatedCourse;
  [key: string]: unknown;
}

/** PUT /courses/:id body — same field vocabulary as create; senders may
 * submit any subset (the dashboard's edit form sends the full set it shows). */
export type UpdateCoursePayload = Partial<CreateCoursePayload>;

/* ── Topics (curriculum modules) ────────────────────────────────────────
 * Module-first authoring: create the topic, then attach lessons to it.
 * POST /courses/:course/topics · PUT /topics/:topic · DELETE /topics/:topic.
 * All respond with the { success, message, data } envelope; a non-owner
 * gets a 422 with { success:false, message } (no data). */

/** POST /courses/:course/topics body. Only title is required —
 * `position` auto-increments server-side when omitted. */
export interface CreateTopicPayload {
  title: string;
  description?: string;
  position?: number;
  [key: string]: unknown;
}

/** PUT /topics/:topic body — all fields optional, send only what changes. */
export type UpdateTopicPayload = Partial<CreateTopicPayload>;

export interface TopicRecord {
  id: number;
  course_id?: number;
  title: string;
  description?: string | null;
  position?: number;
  [key: string]: unknown;
}

export interface TopicResponse {
  success?: boolean;
  message?: string;
  data?: TopicRecord;
  [key: string]: unknown;
}

/** DELETE /courses/:id — plain success/message envelope. */
export interface DeleteCourseResponse {
  success?: boolean;
  message?: string;
  [key: string]: unknown;
}

/** POST /courses/:course/lessons body. title + type required. Attach to an
 * existing topic via `topic_id`, OR create one inline via `new_topic_title`
 * (+ optional description) — send one or the other, not both. `vimeo_id`
 * carries the video for type "vimeo"; `content` the body for type "text",
 * the video link for type "youtube", and the uploaded file's URL for type
 * "file" (from uploadLessonFile). */
export interface CreateLessonPayload {
  title: string;
  type: "vimeo" | "youtube" | "text" | "file" | string;
  vimeo_id?: string | null;
  content?: string | null;
  is_preview?: boolean;
  topic_id?: number | string | null;
  new_topic_title?: string;
  new_topic_description?: string;
  [key: string]: unknown;
}

export interface CreatedLesson {
  id: number;
  course_id: number;
  topic_id?: number | null;
  title: string;
  type: string;
  position?: number;
  [key: string]: unknown;
}

/** POST /courses/:course/lesson-file-upload — the stored attachment's public URL. */
export interface LessonFileUploadResponse {
  success?: boolean;
  message?: string;
  data?: { file_url: string };
  [key: string]: unknown;
}

export interface CreateLessonResponse {
  success?: boolean;
  message?: string;
  data?: CreatedLesson;
  [key: string]: unknown;
}

export const courses = {
  /** Paginated course listing (published courses only). `search` and
   * `level` are confirmed-working query params live; `category_id`
   * filtering could not be confirmed (no working category-list endpoint to
   * source ids from) so it's intentionally not offered here. */
  list: (params: CourseListParams = {}, opts: { signal?: AbortSignal } = {}) =>
    apiClient.get<CoursesListResponse>("/courses", { params: { ...params }, signal: opts.signal }),

  /** Single course by numeric id. Optional bearer token unlocks full lesson
   * video URLs for enrolled users — the shared apiClient always rides the
   * session cookie, so this happens automatically when signed in. */
  get: (id: number | string) => apiClient.get<CourseEnvelope>(`/courses/${id}`),

  /** Single course by slug — same content-gating behaviour as get(). */
  getBySlug: (slug: string) => apiClient.get<CourseEnvelope>(`/courses/${encodeURIComponent(slug)}`),

  /** Category lookup for the instructor create-course form (the read side
   * has no category list — see the `category` note on Course). */
  categories: () => apiClient.get<CourseCategoriesResponse>("/course-categories"),

  /** Create a course (Instructor/Admin). Returns the new course's id/slug. */
  create: (payload: CreateCoursePayload) =>
    apiClient.post<CreateCourseResponse>("/courses", payload),

  /** Update a course (Instructor/Admin) — standard resource route
   * (PUT /courses/:id), same field vocabulary as create. */
  update: (id: number | string, payload: UpdateCoursePayload) =>
    apiClient.put<CreateCourseResponse>(`/courses/${id}`, payload),

  /** Delete a course (Instructor/Admin) — standard resource route. */
  remove: (id: number | string) =>
    apiClient.delete<DeleteCourseResponse>(`/courses/${id}`),

  /** Create a topic/module on a course — position auto-increments if omitted. */
  createTopic: (courseId: number | string, payload: CreateTopicPayload) =>
    apiClient.post<TopicResponse>(`/courses/${courseId}/topics`, payload),

  /** Update a topic/module — send only the fields that change. */
  updateTopic: (topicId: number | string, payload: UpdateTopicPayload) =>
    apiClient.put<TopicResponse>(`/topics/${topicId}`, payload),

  /** Delete a topic/module (its lessons go with it). No body. */
  removeTopic: (topicId: number | string) =>
    apiClient.delete<TopicResponse>(`/topics/${topicId}`),

  /** Add a lesson to a course. Pass a FormData for type "file" lessons —
   * axios clears the JSON content-type for FormData bodies, so the multipart
   * boundary is set by the browser. */
  createLesson: (courseId: number | string, payload: CreateLessonPayload | FormData) =>
    apiClient.post<CreateLessonResponse>(`/courses/${courseId}/lessons`, payload),

  /** Store a "file" lesson's attachment (PDF/JPG/PNG, max 5 MB) and get its URL
   * back — then createLesson with type "file" and that URL as `content`. */
  uploadLessonFile: (courseId: number | string, file: File) => {
    const form = new FormData();
    form.append("lesson_file", file);
    return apiClient.post<LessonFileUploadResponse>(`/courses/${courseId}/lesson-file-upload`, form);
  },

  /** Enroll the authenticated user (free path — paid courses should
   * go through createCheckoutOrder + verifyCheckoutPayment instead). */
  enroll: (payload: EnrollPayload) => apiClient.post<EnrollmentEnvelope>("/courses/enroll", payload),

  /** The authenticated user's enrollments (course + instructor + live class relations). */
  myEnrollments: () => apiClient.get<EnrollmentsEnvelope>("/enrollments"),

  enrollment: (id: number | string) => apiClient.get<EnrollmentEnvelope>(`/enrollments/${id}`),

  /* ── Pricing: the fixed installment plan (instructor/admin only) ──
   * Behind the same author + ownership gates as the other authoring calls, so
   * an instructor can only reprice a course they actually teach. */

  /** This course's installment plan, with the price it has to add up to. */
  installments: (courseId: number | string) =>
    apiClient.get<CourseInstallmentsResponse>(`/courses/${courseId}/installments`),

  /** Replace the whole plan. Pass `{ installments: [] }` to clear it. */
  saveInstallments: (courseId: number | string, payload: SaveInstallmentsPayload) =>
    apiClient.put<CourseInstallmentsResponse>(`/courses/${courseId}/installments`, payload),

  /** Who on this course still owes money, and how much has been collected. */
  outstanding: (courseId: number | string) =>
    apiClient.get<CourseOutstandingResponse>(`/courses/${courseId}/outstanding`),

  /** Validate a sitewide coupon against one course's price. */
  validateCoupon: (courseId: number | string, payload: ValidateCouponPayload) =>
    apiClient.post<ValidateCouponResponse>(`/courses/${courseId}/coupons/validate`, payload),

  /** Create a Razorpay order for the course price minus any coupon discount. */
  createCheckoutOrder: (courseId: number | string, payload: CourseCheckoutOrderPayload = {}) =>
    apiClient.post<CourseCheckoutOrderResponse>(`/courses/${courseId}/checkout/order`, payload),

  /** Verify the Razorpay payment and create the Enrollment. */
  verifyCheckoutPayment: (courseId: number | string, payload: CourseCheckoutVerifyPayload) =>
    apiClient.post<CourseCheckoutVerifyResponse>(`/courses/${courseId}/checkout/verify`, payload),

  /** Progress for the authenticated user's enrollment. 403s (via ApiRequestError) when not enrolled. */
  progress: (courseId: number | string) =>
    apiClient.get<CourseProgressEnvelope>(`/courses/${courseId}/progress`),

  /** Idempotent — answers with the course's updated progress. 403 when not
   * enrolled (active or completed enrollment required). */
  completeLesson: (courseId: number | string, lessonId: number | string) =>
    apiClient.post<CourseProgressEnvelope>(`/courses/${courseId}/lessons/${lessonId}/complete`, {}),

  /** Undo of completeLesson — answers with the course's updated progress. */
  uncompleteLesson: (courseId: number | string, lessonId: number | string) =>
    apiClient.delete<CourseProgressEnvelope>(`/courses/${courseId}/lessons/${lessonId}/complete`),
};

/** Fixes the observed "https://vimeo.com/https://vimeo.com/…" duplicated-
 * prefix data bug on lesson video URLs. Leaves already-correct URLs alone. */
export function cleanVideoUrl(url: string | null | undefined): string | null {
  if (!url) return null;
  const doubled = "https://vimeo.com/https://vimeo.com/";
  if (url.startsWith(doubled)) return `https://vimeo.com/${url.slice(doubled.length)}`;
  return url;
}

/** A lesson's video as a link to open in its own tab ("Watch →"). The API's
 * `videoUrl` is made for an <iframe> - YouTube lessons arrive as
 * youtube.com/embed/<id>, which YouTube won't play when opened directly (an
 * unlisted video shows "Video unavailable") - so this maps it back to the
 * watch page. A bare Vimeo id becomes its vimeo.com page. */
export function videoWatchUrl(url: string | null | undefined): string | null {
  const clean = cleanVideoUrl(url);
  if (!clean) return null;
  const youtube = clean.match(/youtube(?:-nocookie)?\.com\/embed\/([\w-]{11})/);
  if (youtube) return `https://www.youtube.com/watch?v=${youtube[1]}`;
  const vimeoPlayer = clean.match(/player\.vimeo\.com\/video\/(\d+)/);
  if (vimeoPlayer) return `https://vimeo.com/${vimeoPlayer[1]}`;
  if (/^\d+$/.test(clean.trim())) return `https://vimeo.com/${clean.trim()}`;
  return clean;
}
